import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Flex, VStack, Heading, Text, Spinner, Badge, Button,
  Card, CardHeader, CardBody, SimpleGrid, Stat, StatLabel, StatNumber,
  Table, Thead, Tbody, Tr, Th, Td, IconButton,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { formatCountry } from '../../utils/country';
import { SubscriptionPanel } from '../subscriptions/SubscriptionPanel';
import { ActivitySection } from './ActivitySection';
import { QuotaTab } from './components/QuotaTab';
import { OrgDeleteDialog } from './components/OrgDeleteDialog';

const cardShadow = '0px 18px 40px rgba(112, 144, 176, 0.12)';

const statusColor: Record<string, string> = {
  active: 'green',
  suspended: 'red',
  pending: 'orange',
  cancelled: 'gray',
};

interface OrganizationLegalAddress {
  streetAddress: string;
  city: string;
  stateProvince: string | null;
  postalCode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  externalAddressId: string | null;
}

interface OrganizationDetail {
  id: string;
  name: string;
  email: string | null;
  status: string;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  memberCount: number;
  locationCount: number;
  createdAt: string;
  updatedAt: string;
  members: Array<{ id: string; email: string; fullName: string; role: string; joinedAt: string }>;
  locations: Array<{ id: string; name: string; type: string; status: string }>;
  // 2026-04-17 refinement — minimal legal field set exposed by the admin API.
  // All nullable because newly-created organizations that haven't finished
  // onboarding won't have them populated yet.
  companyName?: string | null;
  registrationNumber?: string | null;
  vatNumber?: string | null;
  country?: string | null;
  legalAddress?: OrganizationLegalAddress | null;
}

interface ActivitySummary {
  playbackHours: number;
  activeLocations: number;
  recentEvents: Array<{ timestamp: string; action: string; details: string }>;
}

export function OrganizationDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = React.useState(false);

  const { data: org, isLoading } = useQuery({
    queryKey: ['admin-organization', id],
    queryFn: () => apiClient.get<OrganizationDetail>(`/admin/organizations/${id}`),
    enabled: Boolean(id),
  });

  const { data: activity } = useQuery({
    queryKey: ['admin-org-activity', id],
    queryFn: () => apiClient.get<ActivitySummary>(`/admin/organizations/${id}/activity`, { days: 30 }),
    enabled: Boolean(id),
  });

  if (isLoading) return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;
  if (!org) return <Text>{t('admin.common.noResults')}</Text>;

  return (
    <VStack spacing={5} align="stretch" pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="organization-detail-page">
      {/* Row 1 — Header Card */}
      <Card borderRadius="20px" boxShadow={cardShadow}>
        <CardBody>
          <Flex align="center" gap={4}>
            <IconButton
              aria-label={t('common.back')}
              icon={<ArrowBackIcon />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            />
            <Heading size="lg" flex={1}>{org.name}</Heading>
            <Badge colorScheme={statusColor[org.status] ?? 'gray'} fontSize="sm" px={3} py={1} borderRadius="full">
              {org.status}
            </Badge>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              borderRadius="12px"
              isDisabled={org.status === 'deleting'}
              onClick={() => setShowDelete(true)}
              data-testid="org-delete-button"
            >
              {t('admin.organizations.delete.action')}
            </Button>
          </Flex>
          <Flex mt={3} gap={6} ml={12} color="gray.500" fontSize="sm">
            <Text>{t('admin.organizations.owner')}: {org.email ?? '—'}</Text>
            <Text>{t('admin.organizations.createdAt')}: {new Date(org.createdAt).toLocaleDateString()}</Text>
            <Text>{t('admin.organizations.updatedAt')}: {new Date(org.updatedAt).toLocaleDateString()}</Text>
          </Flex>
        </CardBody>
      </Card>

      {/* Row 1b — Company Legal Details (2026-04-17 refinement) */}
      <Card borderRadius="20px" boxShadow={cardShadow}>
        <CardHeader pb={0}>
          <Heading size="md">{t('admin.organizations.companyDetails')}</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} data-testid="org-company-details-grid">
            <CompanyField label={t('admin.organizations.companyName')} value={org.companyName} />
            <CompanyField
              label={t('admin.organizations.registrationNumber')}
              value={org.registrationNumber}
              testId="org-registration-number"
            />
            <CompanyField label={t('admin.organizations.vatNumber')} value={org.vatNumber} />
            <CompanyField label={t('admin.organizations.country')} value={formatCountry(org.country, i18n.language)} />
          </SimpleGrid>

          {org.legalAddress && (
            <>
              <Text mt={5} mb={3} fontSize="xs" fontWeight={700} color="gray.500" textTransform="uppercase">
                {t('admin.organizations.legalAddress')}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} data-testid="org-legal-address-grid">
                <CompanyField
                  label={t('admin.organizations.street')}
                  value={org.legalAddress.streetAddress}
                  gridColumn={{ md: 'span 2' }}
                />
                <CompanyField label={t('admin.organizations.city')} value={org.legalAddress.city} />
                <CompanyField label={t('admin.organizations.stateProvince')} value={org.legalAddress.stateProvince} />
                <CompanyField label={t('admin.organizations.postalCode')} value={org.legalAddress.postalCode} />
                {org.legalAddress.latitude != null && org.legalAddress.longitude != null && (
                  <CompanyField
                    label={t('admin.organizations.coordinates')}
                    value={`${org.legalAddress.latitude}, ${org.legalAddress.longitude}`}
                  />
                )}
              </SimpleGrid>
            </>
          )}
        </CardBody>
      </Card>

      {/* Row 2 — KPI Stats */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5}>
        <Card borderRadius="20px" boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>{t('admin.organizations.plan')}</StatLabel>
              <StatNumber fontSize="xl">
                <Badge colorScheme="purple" fontSize="md">{org.subscriptionPlan ?? '—'}</Badge>
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="20px" boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>{t('admin.organizations.members')}</StatLabel>
              <StatNumber>{org.memberCount}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="20px" boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>{t('admin.organizations.locations')}</StatLabel>
              <StatNumber>{org.locationCount}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="20px" boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>{t('admin.organizations.playbackHours')}</StatLabel>
              <StatNumber>{activity?.playbackHours ?? 0}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Row 3 — Subscription + Quotas */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
        <Card borderRadius="20px" boxShadow={cardShadow}>
          <CardHeader pb={0}>
            <Heading size="md">{t('admin.subscriptions.title')}</Heading>
          </CardHeader>
          <CardBody>
            <SubscriptionPanel organizationId={id!} />
          </CardBody>
        </Card>
        <Card borderRadius="20px" boxShadow={cardShadow}>
          <CardHeader pb={0}>
            <Heading size="md">{t('admin.quotas.title')}</Heading>
          </CardHeader>
          <CardBody>
            <QuotaTab organizationId={id!} />
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Row 4 — Members + Locations */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
        <Card borderRadius="20px" boxShadow={cardShadow}>
          <CardHeader pb={0}>
            <Heading size="md">{t('admin.organizations.members')}</Heading>
          </CardHeader>
          <CardBody>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('admin.organizations.email')}</Th>
                  <Th>{t('admin.organizations.memberName')}</Th>
                  <Th>{t('admin.organizations.role')}</Th>
                  <Th>{t('admin.organizations.joined')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {org.members?.map(m => (
                  <Tr key={m.id}>
                    <Td>{m.email}</Td>
                    <Td>{m.fullName}</Td>
                    <Td><Badge>{m.role}</Badge></Td>
                    <Td>{new Date(m.joinedAt).toLocaleDateString()}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
        <Card borderRadius="20px" boxShadow={cardShadow}>
          <CardHeader pb={0}>
            <Heading size="md">{t('admin.organizations.locations')}</Heading>
          </CardHeader>
          <CardBody>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('admin.organizations.name')}</Th>
                  <Th>{t('admin.organizations.locationType')}</Th>
                  <Th>{t('admin.organizations.status')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {org.locations?.map(l => (
                  <Tr key={l.id}>
                    <Td>{l.name}</Td>
                    <Td>{l.type}</Td>
                    <Td><Badge colorScheme={l.status === 'active' ? 'green' : 'gray'}>{l.status}</Badge></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Row 5 — Activity */}
      <Card borderRadius="20px" boxShadow={cardShadow}>
        <CardHeader pb={0}>
          <Heading size="md">{t('admin.organizations.activity')}</Heading>
        </CardHeader>
        <CardBody>
          <ActivitySection organizationId={id!} />
        </CardBody>
      </Card>

      {id && (
        <OrgDeleteDialog
          orgId={id}
          orgName={org.name}
          isOpen={showDelete}
          onClose={() => setShowDelete(false)}
        />
      )}
    </VStack>
  );
}

interface CompanyFieldProps {
  label: string;
  value?: string | null;
  testId?: string;
  gridColumn?: { base?: string; md?: string };
}

function CompanyField({ label, value, testId, gridColumn }: CompanyFieldProps) {
  const display = value && value.length > 0 ? value : '—';
  return (
    <VStack align="stretch" spacing={1} gridColumn={gridColumn} data-testid={testId}>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase">
        {label}
      </Text>
      <Text fontWeight={600} fontSize="sm">
        {display}
      </Text>
    </VStack>
  );
}
